/**
 * AgentManager.jsx — AI Agent platform console.
 *
 * Four tabs:
 *   • Agents    — CRUD for AI agents (instructions, model, intents, tools,
 *                 limits, handoff, enable).
 *   • Tools     — CRUD for configurable API tools the agent can call.
 *   • Activity  — per-task execution summaries (Task / Status / Tools / Result /
 *                 time) — the admin-only view from spec §14.
 *   • Analytics — agent vs chatbot split, tool-call success, task completion,
 *                 most-used tools, most-common intents (spec §11).
 *
 * Talks to admin_service /agents (AgentController). Backward-compatible: this is
 * a new, permission-gated console page; nothing else changes.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table, Tabs, Tab } from "react-bootstrap";
import { Bot, Plus, Pencil, Trash2, Wrench, Activity, BarChart3, ChevronDown, ChevronRight } from "lucide-react";
import APICall from "../../APICalls/APICall";
import AgentAPI from "../../APICalls/agentAPI";
import { usePermission } from "../../Context/AuthContext";
import { useToast } from "../../components/useToast";
import { useConfirm } from "../../components/useConfirm";
import { formatDateTime } from "../../utils/datetime";

const CONFIRM_LABEL = { none: ["Auto", "secondary"], customer: ["Ask customer", "info"], human: ["Human approval", "warning"] };
const STATUS_VARIANT = { ACTIVE: "success", DRAFT: "secondary", DELETED: "danger" };

export default function AgentManager() {
  const [key, setKey] = useState("agents");
  return (
    <div className="p-3">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Bot size={22} />
        <h4 className="m-0">AI Agents</h4>
        <span className="text-muted ms-2" style={{ fontSize: 13 }}>
          Configure agents that take actions via tools — alongside your existing chatbot.
        </span>
      </div>
      <Tabs activeKey={key} onSelect={setKey} className="mb-3">
        <Tab eventKey="agents"    title={<span><Bot size={15} className="me-1" />Agents</span>}>
          <AgentsTab active={key === "agents"} />
        </Tab>
        <Tab eventKey="tools"     title={<span><Wrench size={15} className="me-1" />Tools</span>}>
          <ToolsTab active={key === "tools"} />
        </Tab>
        <Tab eventKey="activity"  title={<span><Activity size={15} className="me-1" />Activity</span>}>
          <ActivityTab active={key === "activity"} />
        </Tab>
        <Tab eventKey="analytics" title={<span><BarChart3 size={15} className="me-1" />Analytics</span>}>
          <AnalyticsTab active={key === "analytics"} />
        </Tab>
      </Tabs>
    </div>
  );
}

/* ======================================================================== */
/* AGENTS                                                                    */
/* ======================================================================== */
function AgentsTab({ active }) {
  const { canAdd, canEdit, canDelete } = usePermission("/AI-Agents");
  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmDialog } = useConfirm();
  const [agents, setAgents] = useState([]);
  const [tools, setTools] = useState([]);
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // agent object or {} for new

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([AgentAPI.listAgents(), AgentAPI.listTools()]);
      setAgents(a?.agents || []);
      setTools(t?.tools || []);
      try {
        const it = await APICall.getT("/intents/intents");
        setIntents((it || []).map(x => x.intent_name || x.name).filter(Boolean));
      } catch { /* intents optional */ }
    } catch (e) {
      showToast(e?.message || "Failed to load agents.", "danger");
    } finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const onToggle = async (a) => {
    try { await AgentAPI.toggleAgent(a.id); showToast(`Agent ${a.enabled ? "disabled" : "enabled"}.`, "success"); load(); }
    catch (e) { showToast(e?.message || "Toggle failed.", "danger"); }
  };
  const onDelete = async (a) => {
    if (!await confirm(`Delete agent "${a.name}"? This cannot be undone.`)) return;
    try { await AgentAPI.deleteAgent(a.id); showToast("Agent deleted.", "warning"); load(); }
    catch (e) { showToast(e?.message || "Delete failed.", "danger"); }
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        {canAdd && <Button size="sm" onClick={() => setEditing({})}><Plus size={15} className="me-1" />New Agent</Button>}
      </div>
      <Card>
        <Table hover responsive className="m-0 align-middle">
          <thead>
            <tr><th>S.No</th><th>Name</th><th>Model</th><th>Intents</th><th>Tools</th><th>Handoff</th><th>Status</th><th>Enabled</th><th className="text-end">Actions</th></tr>
          </thead>
          <tbody>
            {agents.length === 0 && <tr><td colSpan={9} className="text-center text-muted py-4">No agents yet. Create one to handle action requests.</td></tr>}
            {agents.map((a, i) => (
              <tr key={a.id}>
                <td>{i + 1}</td>
                <td><strong>{a.name}</strong><div className="text-muted" style={{ fontSize: 12 }}>{a.description}</div></td>
                <td><Badge bg="light" text="dark">{a.provider}{a.model ? ` · ${a.model}` : ""}</Badge></td>
                <td>{(a.intent_names || []).length}</td>
                <td>{(a.tool_ids || []).length}</td>
                <td>{a.handoff_enabled ? <Badge bg="info">on</Badge> : <Badge bg="secondary">off</Badge>}</td>
                <td><Badge bg={STATUS_VARIANT[a.status] || "secondary"}>{a.status}</Badge></td>
                <td><Form.Check type="switch" checked={!!a.enabled} disabled={!canEdit} onChange={() => onToggle(a)} /></td>
                <td className="text-end">
                  {canEdit && <Button variant="link" size="sm" className="p-1" onClick={() => setEditing(a)}><Pencil size={15} /></Button>}
                  {canDelete && <Button variant="link" size="sm" className="p-1 text-danger" onClick={() => onDelete(a)}><Trash2 size={15} /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      {editing && (
        <AgentEditor agent={editing} tools={tools} intents={intents}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); showToast("Agent saved.", "success"); }}
          onError={(m) => showToast(m, "danger")} />
      )}
      <ToastContainer /><ConfirmDialog />
    </>
  );
}

function AgentEditor({ agent, tools, intents, onClose, onSaved, onError }) {
  const isNew = !agent.id;
  const [f, setF] = useState(() => ({
    name: agent.name || "", description: agent.description || "",
    system_instructions: agent.system_instructions || "",
    provider: agent.provider || "ollama", model: agent.model || "",
    temperature: agent.temperature ?? 0.2,
    max_tool_calls: agent.max_tool_calls ?? 5,
    max_execution_seconds: agent.max_execution_seconds ?? 30,
    intent_names: agent.intent_names || [], tool_ids: agent.tool_ids || [],
    kb_domains: (agent.kb_domains || []).join(", "),
    handoff_enabled: agent.handoff_enabled ?? true,
    status: agent.status || "DRAFT", enabled: agent.enabled ?? false,
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggleIn = (k, val) => set(k, f[k].includes(val) ? f[k].filter(x => x !== val) : [...f[k], val]);

  const save = async () => {
    if (!f.name.trim()) { onError("Agent name is required."); return; }
    setSaving(true);
    const payload = {
      ...f,
      temperature: parseFloat(f.temperature) || 0,
      max_tool_calls: parseInt(f.max_tool_calls) || 1,
      max_execution_seconds: parseInt(f.max_execution_seconds) || 10,
      kb_domains: f.kb_domains.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) await AgentAPI.createAgent(payload); else await AgentAPI.updateAgent(agent.id, payload);
      onSaved();
    } catch (e) { onError(e?.message || "Save failed."); } finally { setSaving(false); }
  };

  return (
    <Modal show size="lg" onHide={onClose} scrollable>
      <Modal.Header closeButton><Modal.Title>{isNew ? "New Agent" : `Edit: ${agent.name}`}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={8}><Form.Label>Name</Form.Label><Form.Control value={f.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Support Copilot" /></Col>
          <Col md={4}><Form.Label>Status</Form.Label>
            <Form.Select value={f.status} onChange={e => set("status", e.target.value)}>
              <option value="DRAFT">Draft</option><option value="ACTIVE">Active</option>
            </Form.Select>
          </Col>
          <Col md={12}><Form.Label>Description</Form.Label><Form.Control value={f.description} onChange={e => set("description", e.target.value)} /></Col>
          <Col md={12}><Form.Label>System instructions</Form.Label>
            <Form.Control as="textarea" rows={4} value={f.system_instructions} onChange={e => set("system_instructions", e.target.value)}
              placeholder="How the agent should behave. Tools it may use are provided automatically." />
          </Col>
          <Col md={4}><Form.Label>Provider</Form.Label>
            <Form.Select value={f.provider} onChange={e => set("provider", e.target.value)}>
              <option value="ollama">Ollama (local)</option>
            </Form.Select>
          </Col>
          <Col md={4}><Form.Label>Model</Form.Label><Form.Control value={f.model} onChange={e => set("model", e.target.value)} placeholder="qwen3:8b" /></Col>
          <Col md={4}><Form.Label>Temperature</Form.Label><Form.Control type="number" step="0.1" min="0" max="2" value={f.temperature} onChange={e => set("temperature", e.target.value)} /></Col>
          <Col md={6}><Form.Label>Max tool calls</Form.Label><Form.Control type="number" min="1" value={f.max_tool_calls} onChange={e => set("max_tool_calls", e.target.value)} /></Col>
          <Col md={6}><Form.Label>Max execution time (s)</Form.Label><Form.Control type="number" min="5" value={f.max_execution_seconds} onChange={e => set("max_execution_seconds", e.target.value)} /></Col>

          <Col md={12}><Form.Label>Action intents <span className="text-muted">(messages with these intents go to the agent)</span></Form.Label>
            <div className="border rounded p-2" style={{ maxHeight: 140, overflowY: "auto" }}>
              {intents.length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>No intents found. You can still save; add intents later.</div>}
              {intents.map(name => (
                <Form.Check inline key={name} type="checkbox" label={name} id={`int-${name}`}
                  checked={f.intent_names.includes(name)} onChange={() => toggleIn("intent_names", name)} />
              ))}
            </div>
          </Col>
          <Col md={12}><Form.Label>Tools the agent may use</Form.Label>
            <div className="border rounded p-2" style={{ maxHeight: 140, overflowY: "auto" }}>
              {tools.length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>No tools yet — create some in the Tools tab.</div>}
              {tools.map(t => (
                <Form.Check key={t.id} type="checkbox" id={`tool-${t.id}`}
                  label={<span>{t.name} <span className="text-muted" style={{ fontSize: 12 }}>— {t.description}</span></span>}
                  checked={f.tool_ids.includes(t.id)} onChange={() => toggleIn("tool_ids", t.id)} />
              ))}
            </div>
          </Col>
          <Col md={12}><Form.Label>Knowledge Base domains <span className="text-muted">(comma-separated, optional)</span></Form.Label>
            <Form.Control value={f.kb_domains} onChange={e => set("kb_domains", e.target.value)} placeholder="orders, billing" /></Col>

          <Col md={6}><Form.Check type="switch" label="Escalate to a human when the agent can't finish" checked={f.handoff_enabled} onChange={e => set("handoff_enabled", e.target.checked)} /></Col>
          <Col md={6}><Form.Check type="switch" label="Enabled (live)" checked={f.enabled} onChange={e => set("enabled", e.target.checked)} /></Col>
        </Row>
        {f.enabled && f.status !== "ACTIVE" && <div className="text-warning mt-2" style={{ fontSize: 13 }}>Note: the agent only runs when status is Active AND enabled.</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save agent"}</Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ======================================================================== */
/* TOOLS                                                                     */
/* ======================================================================== */
function ToolsTab({ active }) {
  const { canAdd, canEdit, canDelete } = usePermission("/AI-Agents");
  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmDialog } = useConfirm();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const t = await AgentAPI.listTools(); setTools(t?.tools || []); }
    catch (e) { showToast(e?.message || "Failed to load tools.", "danger"); }
    finally { setLoading(false); }
  }, [showToast]);
  useEffect(() => { if (active) load(); }, [active, load]);

  const onDelete = async (t) => {
    if (!await confirm(`Delete tool "${t.name}"?`)) return;
    try { await AgentAPI.deleteTool(t.id); showToast("Tool deleted.", "warning"); load(); }
    catch (e) { showToast(e?.message || "Delete failed.", "danger"); }
  };

  if (loading) return <Spinner animation="border" />;
  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        {canAdd && <Button size="sm" onClick={() => setEditing({})}><Plus size={15} className="me-1" />New Tool</Button>}
      </div>
      <Card>
        <Table hover responsive className="m-0 align-middle">
          <thead><tr><th>S.No</th><th>Name</th><th>Method</th><th>Endpoint</th><th>Confirmation</th><th>Enabled</th><th className="text-end">Actions</th></tr></thead>
          <tbody>
            {tools.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">No tools yet.</td></tr>}
            {tools.map((t, i) => {
              const [lbl, variant] = CONFIRM_LABEL[t.confirmation_mode] || CONFIRM_LABEL.none;
              return (
                <tr key={t.id}>
                  <td>{i + 1}</td>
                  <td><strong>{t.name}</strong><div className="text-muted" style={{ fontSize: 12 }}>{t.description}</div></td>
                  <td><Badge bg="light" text="dark">{t.method}</Badge></td>
                  <td style={{ fontSize: 12, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(t.base_url || "") + (t.path || "")}</td>
                  <td><Badge bg={variant}>{lbl}</Badge></td>
                  <td>{t.enabled ? <Badge bg="success">on</Badge> : <Badge bg="secondary">off</Badge>}</td>
                  <td className="text-end">
                    {canEdit && <Button variant="link" size="sm" className="p-1" onClick={() => setEditing(t)}><Pencil size={15} /></Button>}
                    {canDelete && <Button variant="link" size="sm" className="p-1 text-danger" onClick={() => onDelete(t)}><Trash2 size={15} /></Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>
      {editing && <ToolEditor tool={editing} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); showToast("Tool saved.", "success"); }}
        onError={(m) => showToast(m, "danger")} />}
      <ToastContainer /><ConfirmDialog />
    </>
  );
}

function ToolEditor({ tool, onClose, onSaved, onError }) {
  const isNew = !tool.id;
  const [f, setF] = useState(() => ({
    name: tool.name || "", description: tool.description || "",
    input_schema: JSON.stringify(tool.input_schema || { type: "object", properties: {}, required: [] }, null, 2),
    base_url: tool.base_url || "", path: tool.path || "", method: tool.method || "GET",
    request_mapping: JSON.stringify(tool.request_mapping || {}, null, 2),
    response_mapping_path: (tool.response_mapping && tool.response_mapping.path) || "",
    response_format: tool.response_format || "auto",
    auth_type: tool.auth_type || "none",
    auth_key: "", auth_value: "", auth_location: "header",
    timeout_seconds: tool.timeout_seconds ?? 15, max_retries: tool.max_retries ?? 1,
    confirmation_mode: tool.confirmation_mode || "none", enabled: tool.enabled ?? true,
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) { onError("Tool name is required."); return; }
    let schema, reqMap;
    try { schema = JSON.parse(f.input_schema || "{}"); } catch { onError("Input schema is not valid JSON."); return; }
    try { reqMap = JSON.parse(f.request_mapping || "{}"); } catch { onError("Request mapping is not valid JSON."); return; }
    const payload = {
      name: f.name, description: f.description, input_schema: schema,
      base_url: f.base_url, path: f.path, method: f.method,
      request_mapping: reqMap,
      response_mapping: f.response_mapping_path ? { path: f.response_mapping_path } : {},
      response_format: f.response_format, auth_type: f.auth_type,
      timeout_seconds: parseInt(f.timeout_seconds) || 15, max_retries: parseInt(f.max_retries) || 0,
      confirmation_mode: f.confirmation_mode, enabled: f.enabled,
    };
    // Only send auth_config when the user actually typed a new secret.
    if (f.auth_type !== "none" && (f.auth_value || f.auth_key)) {
      if (f.auth_type === "bearer") payload.auth_config = { token: f.auth_value };
      else if (f.auth_type === "api_key") payload.auth_config = { key: f.auth_key || "X-API-Key", value: f.auth_value, location: f.auth_location };
      else if (f.auth_type === "basic") payload.auth_config = { header: f.auth_value };
    }
    setSaving(true);
    try {
      if (isNew) await AgentAPI.createTool(payload); else await AgentAPI.updateTool(tool.id, payload);
      onSaved();
    } catch (e) { onError(e?.message || "Save failed."); } finally { setSaving(false); }
  };

  return (
    <Modal show size="lg" onHide={onClose} scrollable>
      <Modal.Header closeButton><Modal.Title>{isNew ? "New Tool" : `Edit: ${tool.name}`}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={6}><Form.Label>Name <span className="text-muted">(the LLM sees this)</span></Form.Label>
            <Form.Control value={f.name} onChange={e => set("name", e.target.value)} placeholder="get_order_status" /></Col>
          <Col md={6}><Form.Label>Confirmation before running</Form.Label>
            <Form.Select value={f.confirmation_mode} onChange={e => set("confirmation_mode", e.target.value)}>
              <option value="none">None — run automatically</option>
              <option value="customer">Ask the customer (yes/no)</option>
              <option value="human">Human approval (route to agent)</option>
            </Form.Select>
          </Col>
          <Col md={12}><Form.Label>Description <span className="text-muted">(tells the LLM when to use it)</span></Form.Label>
            <Form.Control value={f.description} onChange={e => set("description", e.target.value)} placeholder="Look up the delivery status of an order by its order number" /></Col>
          <Col md={12}><Form.Label>Input parameters (JSON Schema)</Form.Label>
            <Form.Control as="textarea" rows={6} style={{ fontFamily: "monospace", fontSize: 12 }} value={f.input_schema} onChange={e => set("input_schema", e.target.value)} /></Col>
          <Col md={3}><Form.Label>Method</Form.Label>
            <Form.Select value={f.method} onChange={e => set("method", e.target.value)}>
              {["GET", "POST", "PUT", "PATCH", "DELETE"].map(m => <option key={m}>{m}</option>)}
            </Form.Select>
          </Col>
          <Col md={5}><Form.Label>Base URL</Form.Label><Form.Control value={f.base_url} onChange={e => set("base_url", e.target.value)} placeholder="https://api.example.com" /></Col>
          <Col md={4}><Form.Label>Path</Form.Label><Form.Control value={f.path} onChange={e => set("path", e.target.value)} placeholder="/orders/{{order_number}}" /></Col>
          <Col md={12}><Form.Label>Request body mapping (JSON, uses <code>{"{{param}}"}</code>)</Form.Label>
            <Form.Control as="textarea" rows={3} style={{ fontFamily: "monospace", fontSize: 12 }} value={f.request_mapping} onChange={e => set("request_mapping", e.target.value)} placeholder='{ "order": "{{order_number}}" }' /></Col>
          <Col md={8}><Form.Label>Response path <span className="text-muted">(dot path to the useful value)</span></Form.Label>
            <Form.Control value={f.response_mapping_path} onChange={e => set("response_mapping_path", e.target.value)} placeholder="data.status" /></Col>
          <Col md={4}><Form.Label>Response format</Form.Label>
            <Form.Select value={f.response_format} onChange={e => set("response_format", e.target.value)}>
              {["auto", "text", "table", "cards", "images", "json"].map(m => <option key={m}>{m}</option>)}
            </Form.Select>
          </Col>
          <Col md={4}><Form.Label>Auth type</Form.Label>
            <Form.Select value={f.auth_type} onChange={e => set("auth_type", e.target.value)}>
              <option value="none">None</option><option value="bearer">Bearer token</option>
              <option value="api_key">API key</option><option value="basic">Basic (header)</option>
            </Form.Select>
          </Col>
          {f.auth_type === "api_key" && <>
            <Col md={4}><Form.Label>Key name</Form.Label><Form.Control value={f.auth_key} onChange={e => set("auth_key", e.target.value)} placeholder="X-API-Key" /></Col>
            <Col md={4}><Form.Label>Location</Form.Label><Form.Select value={f.auth_location} onChange={e => set("auth_location", e.target.value)}><option value="header">Header</option><option value="query">Query</option></Form.Select></Col>
          </>}
          {f.auth_type !== "none" && <Col md={f.auth_type === "api_key" ? 12 : 8}><Form.Label>{f.auth_type === "basic" ? "Authorization header value" : "Secret value"} <span className="text-muted">(stored encrypted; leave blank to keep existing)</span></Form.Label>
            <Form.Control type="password" value={f.auth_value} onChange={e => set("auth_value", e.target.value)} autoComplete="new-password" /></Col>}
          <Col md={4}><Form.Label>Timeout (s)</Form.Label><Form.Control type="number" min="1" max="30" value={f.timeout_seconds} onChange={e => set("timeout_seconds", e.target.value)} /></Col>
          <Col md={4}><Form.Label>Max retries</Form.Label><Form.Control type="number" min="0" max="3" value={f.max_retries} onChange={e => set("max_retries", e.target.value)} /></Col>
          <Col md={4} className="d-flex align-items-end"><Form.Check type="switch" label="Enabled" checked={f.enabled} onChange={e => set("enabled", e.target.checked)} /></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save tool"}</Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ======================================================================== */
/* ACTIVITY (execution summaries — spec §14)                                 */
/* ======================================================================== */
function ActivityTab({ active }) {
  const { showToast, ToastContainer } = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // task id expanded
  const [callsByTask, setCallsByTask] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const t = await AgentAPI.tasks(); setTasks(t?.tasks || []); }
    catch (e) { showToast(e?.message || "Failed to load activity.", "danger"); }
    finally { setLoading(false); }
  }, [showToast]);
  useEffect(() => { if (active) load(); }, [active, load]);

  const expand = async (task) => {
    if (open === task.id) { setOpen(null); return; }
    setOpen(task.id);
    if (!callsByTask[task.id]) {
      try { const c = await AgentAPI.toolCalls(`?task_id=${task.id}`); setCallsByTask(p => ({ ...p, [task.id]: c?.tool_calls || [] })); }
      catch { setCallsByTask(p => ({ ...p, [task.id]: [] })); }
    }
  };

  const statusVariant = { completed: "success", failed: "danger", escalated: "warning", running: "info", awaiting_confirmation: "secondary" };
  if (loading) return <Spinner animation="border" />;
  return (
    <>
      <Card>
        <Table hover responsive className="m-0 align-middle">
          <thead><tr><th style={{ width: 30 }} /><th>S.No</th><th>Task</th><th>Status</th><th>Result</th><th>Time</th><th>When</th></tr></thead>
          <tbody>
            {tasks.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">No agent activity yet.</td></tr>}
            {tasks.map((t, i) => (
              <Fragment key={t.id}>
                <tr style={{ cursor: "pointer" }} onClick={() => expand(t)}>
                  <td>{open === t.id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                  <td>{i + 1}</td>
                  <td style={{ maxWidth: 280 }}>{t.goal}</td>
                  <td><Badge bg={statusVariant[t.status] || "secondary"}>{t.status}</Badge></td>
                  <td style={{ maxWidth: 260, fontSize: 12 }}>{t.result_summary}</td>
                  <td>{t.duration_ms != null ? `${(t.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                  <td style={{ fontSize: 12 }}>{t.started_at ? formatDateTime(t.started_at) : ""}</td>
                </tr>
                {open === t.id && (
                  <tr><td /><td colSpan={6}>
                    <div className="p-2 bg-body-secondary rounded">
                      <div className="mb-2"><strong>Execution summary</strong></div>
                      {(callsByTask[t.id] || []).length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>No tool calls recorded for this task.</div>}
                      {(callsByTask[t.id] || []).map(c => (
                        <div key={c.id} className="d-flex justify-content-between border-bottom py-1" style={{ fontSize: 13 }}>
                          <span><Wrench size={13} className="me-1" />{c.tool_name}
                            <span className="text-muted ms-2">{JSON.stringify(c.input)}</span></span>
                          <span><Badge bg={c.status === "success" ? "success" : "danger"} className="me-2">{c.status}</Badge>
                            {c.latency_ms != null ? `${c.latency_ms}ms` : ""}</span>
                        </div>
                      ))}
                      {t.reasoning_summary && <div className="mt-2 text-muted" style={{ fontSize: 12 }}>Note: {t.reasoning_summary}</div>}
                    </div>
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </Table>
      </Card>
      <ToastContainer />
    </>
  );
}

/* ======================================================================== */
/* ANALYTICS (spec §11)                                                      */
/* ======================================================================== */
function AnalyticsTab({ active }) {
  const { showToast, ToastContainer } = useToast();
  const [a, setA] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setA(await AgentAPI.analytics(days)); }
    catch (e) { showToast(e?.message || "Failed to load analytics.", "danger"); }
    finally { setLoading(false); }
  }, [days, showToast]);
  useEffect(() => { if (active) load(); }, [active, load]);

  if (loading || !a) return <Spinner animation="border" />;
  const pct = (n) => `${Math.round((n || 0) * 100)}%`;
  const Stat = ({ label, value, sub }) => (
    <Col md={3} sm={6}><Card className="text-center h-100"><Card.Body>
      <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 600 }}>{value}</div>
      {sub && <div className="text-muted" style={{ fontSize: 11 }}>{sub}</div>}
    </Card.Body></Card></Col>
  );
  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <Form.Select size="sm" style={{ width: 140 }} value={days} onChange={e => setDays(parseInt(e.target.value))}>
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
        </Form.Select>
      </div>
      <Row className="g-3 mb-3">
        <Stat label="Agent conversations" value={a.agent_conversations} sub={`of ${a.total_conversations} total`} />
        <Stat label="Chatbot conversations" value={a.chatbot_conversations} />
        <Stat label="Task completion" value={pct(a.task_completion_rate)} sub={`${a.completed_tasks}/${a.total_tasks} tasks`} />
        <Stat label="Avg execution" value={`${(a.avg_execution_ms / 1000).toFixed(1)}s`} />
        <Stat label="Tool calls" value={a.tool_calls} sub={`${a.successful_tool_calls} ok · ${a.failed_tool_calls} failed`} />
        <Stat label="Failed tasks" value={a.failed_tasks} />
        <Stat label="Escalations" value={a.escalations} />
        <Stat label="Human handoffs" value={a.human_handoffs} />
      </Row>
      <Row className="g-3">
        <Col md={6}><Card><Card.Header>Most used tools</Card.Header>
          <Table className="m-0"><tbody>
            {(a.most_used_tools || []).length === 0 && <tr><td className="text-muted">No tool calls yet.</td></tr>}
            {(a.most_used_tools || []).map(t => <tr key={t.tool}><td>{t.tool}</td><td className="text-end"><Badge bg="secondary">{t.count}</Badge></td></tr>)}
          </tbody></Table></Card></Col>
        <Col md={6}><Card><Card.Header>Most common intents</Card.Header>
          <Table className="m-0"><tbody>
            {(a.most_common_intents || []).length === 0 && <tr><td className="text-muted">No agent intents yet.</td></tr>}
            {(a.most_common_intents || []).map(t => <tr key={t.intent}><td>{t.intent}</td><td className="text-end"><Badge bg="secondary">{t.count}</Badge></td></tr>)}
          </tbody></Table></Card></Col>
      </Row>
      <ToastContainer />
    </>
  );
}
